import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi/useApi";
import { useNotification } from "~hooks/useNotification";
import type { UserPermissionResponse } from "~types/userPermission";
import type { UserTransformed } from "../../types";
import { transformUserPermission } from "../../utilities";
import type { UpsertUserPermissionData } from "../types";

const useUpsertUserPermission = () => {
  const { sport, organizationId, userId } = useParams();
  const request = useApi();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<UserPermissionResponse, Error, UpsertUserPermissionData>({
    mutationFn: async (upsertModel: UpsertUserPermissionData) => {
      let url = `${sport}/o/${organizationId}/users/${userId}/permissions`;
      if (upsertModel.permissionId) {
        url += `/${upsertModel.permissionId}`;
      }
      const method = upsertModel.permissionId ? "PUT" : "POST";
      const body = upsertModel.upsertData;
      return await request(url, { method, body });
    },
    onSuccess: async (response: UserPermissionResponse, upsertModel: UpsertUserPermissionData) => {
      if (response?.data?.length > 0) {
        const existingUser: UserTransformed | undefined = queryClient.getQueryData(["permission-users", sport, organizationId, userId]);

        if (existingUser) {
          const responseResources = response.includes?.resources || {};
          const upsertedPermission = transformUserPermission(response.data[0], false, responseResources);
          if (upsertModel.permissionId) {
            existingUser.permissions = existingUser.permissions.map((permission) => {
              if (permission.permissionId === upsertModel.permissionId) {
                return upsertedPermission;
              }
              return permission;
            });
          } else {
            existingUser.permissions.unshift(upsertedPermission);
          }
        }

        notification.success({
          message: formatMessage({
            id: `permissions.user.role.actions.${upsertModel.permissionId ? "edit" : "add"}.success`,
          }),
          placement: "bottomRight",
        });
      }
    },
  });
};

export default useUpsertUserPermission;
